import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
// import { ColorField } from "app/pages/presentation/module/form/types";
import Div from "@jumbo/shared/Div";
// import { Label } from "recharts";
import {
  RECOMMENDED_PRODUCTS_VIEW_TYPES,
  ZERO_PRICE_OPTIONS,
} from "app/utils/constants/settings";

const FIELD_NAME = "SMARTKIOSK_PRODUCTS";

const SmartkioskProducts = ({
  handleChange,
  expanded,
  hideSectionTitle = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();

  const content = (
    <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={6}>
            <Typography sx={{ width: "33%", flexShrink: 0 }}>
              {t("pages.dsokDetails.products.grid.title")}
            </Typography>
            <Divider />
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.grid.showAddToCartAction"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.grid.showAddToCartAction",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t(
                    "pages.dsokDetails.products.grid.showAddToCartAction"
                  )}
                  checked={
                    values.settings?.smartkiosk?.productSettings?.grid
                      ?.showAddToCartAction
                  }
                />
              </Div>
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.grid.showPrice"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.grid.showPrice",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t("pages.dsokDetails.products.grid.showPrice")}
                  checked={
                    values.settings?.smartkiosk?.productSettings?.grid
                      ?.showPrice
                  }
                />
              </Div>
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div
                sx={{
                  mt: 2,
                  mb: 1,
                  mx: 0.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.grid.showPriceForAuthenticatedUser"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.grid.showPriceForAuthenticatedUser",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t(
                    "pages.dsokDetails.products.grid.showPriceForAuthenticatedUser",
                    { defaultValue: "Zobraziť cenu prihlásenému klientovi" }
                  )}
                  checked={
                    values.settings?.smartkiosk?.productSettings?.grid
                      ?.showPriceForAuthenticatedUser
                  }
                />
                <Tooltip
                  title={t(
                    "pages.dsokDetails.products.grid.showPriceForAuthenticatedUserHelp",
                    {
                      defaultValue:
                        "Ak je vypnuté, prihlásenému klientovi (napr. zamestnancovi firmy) sa ceny produktov nezobrazia. Predvolene zapnuté.",
                    }
                  )}
                >
                  <HelpOutlineIcon
                    fontSize="small"
                    sx={{ color: "text.secondary", cursor: "help", ml: 0.5 }}
                  />
                </Tooltip>
              </Div>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                <JumboTextField
                  select
                  fullWidth
                  name="settings.smartkiosk.productSettings.grid.zeroPriceDisplay"
                  label={t("pages.dsokDetails.products.grid.zeroPriceDisplay", {
                    defaultValue: "Zero price display",
                  })}
                >
                  {ZERO_PRICE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt === "0"
                        ? "0"
                        : t(
                            `pages.dsokDetails.products.grid.zeroPriceOptions.${opt}`,
                            {
                              defaultValue:
                                opt === "free"
                                  ? "Free"
                                  : opt === "included"
                                  ? "Included"
                                  : opt,
                            }
                          )}
                    </MenuItem>
                  ))}
                </JumboTextField>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                <JumboTextField
                  type="number"
                  InputLabelProps={{ required: false }}
                  required
                  fullWidth
                  name="settings.smartkiosk.productSettings.grid.columns"
                  label={t(
                    "pages.dsokDetails.products.recommendedProducts.columns"
                  )}
                  InputProps={{
                    value:
                      values.settings?.smartkiosk?.productSettings?.grid
                        ?.columns,
                    onChange: (e) =>
                      setFieldValue(
                        "settings.smartkiosk.productSettings.grid.columns",
                        e.target.value
                      ),
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <Typography sx={{ width: "33%", flexShrink: 0 }}>
              {t("pages.dsokDetails.products.productVariants.title")}
            </Typography>
            <Grid item xs={12} md={12} lg={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.productVariants.showTitle"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.productVariants.showTitle",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t(
                    "pages.dsokDetails.products.productVariants.showTitle"
                  )}
                  checked={
                    values.settings?.smartkiosk?.productSettings
                      ?.productVariants?.showTitle
                  }
                />
              </Div>
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5, display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.productVariants.showDescription"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.productVariants.showDescription",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t(
                    "pages.dsokDetails.products.productVariants.showDescription",
                    { defaultValue: "Zobraziť popis produktu" }
                  )}
                  checked={
                    values.settings?.smartkiosk?.productSettings
                      ?.productVariants?.showDescription
                  }
                />
                <Tooltip
                  title={t(
                    "pages.dsokDetails.products.productVariants.showDescriptionHelp",
                    {
                      defaultValue:
                        "Ak je povolené, zobrazí sa pri detaile produktu aj popis.",
                    }
                  )}
                >
                  <HelpOutlineIcon
                    fontSize="small"
                    sx={{ color: "text.secondary", cursor: "help", ml: 0.5 }}
                  />
                </Tooltip>
              </Div>
            </Grid>
            <Grid item xs={12} md={12} lg={4}>
              <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                <JumboTextField
                  type="number"
                  InputLabelProps={{ required: false }}
                  required
                  fullWidth
                  name="settings.smartkiosk.productSettings.productVariants.columns"
                  label={t(
                    "pages.dsokDetails.products.recommendedProducts.columns"
                  )}
                  InputProps={{
                    value:
                      values.settings?.smartkiosk?.productSettings
                        ?.productVariants?.columns,
                    onChange: (e) =>
                      setFieldValue(
                        "settings.smartkiosk.productSettings.productVariants.columns",
                        e.target.value
                      ),
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.productVariants.enableQuantitySelection"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.productVariants.enableQuantitySelection",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t(
                    "pages.dsokDetails.products.productVariants.enableQuantitySelection"
                  )}
                  checked={
                    values.settings?.smartkiosk?.productSettings
                      ?.productVariants?.enableQuantitySelection
                  }
                />
              </Div>
            </Grid>
            <Grid item xs={12} md={12} lg={4}>
              <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                <JumboTextField
                  type="number"
                  InputLabelProps={{ required: false }}
                  required
                  fullWidth
                  name="settings.smartkiosk.productSettings.productVariants.maxQuantity"
                  label={t(
                    "pages.dsokDetails.products.productVariants.maxQuantity"
                  )}
                  InputProps={{
                    value:
                      values.settings?.smartkiosk?.productSettings
                        ?.productVariants?.maxQuantity,
                    onChange: (e) =>
                      setFieldValue(
                        "settings.smartkiosk.productSettings.productVariants.maxQuantity",
                        e.target.value
                      ),
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <Typography sx={{ width: "33%", flexShrink: 0 }}>
              {t("pages.dsokDetails.products.recommendedProducts.title")}
            </Typography>
            <Grid item xs={12} md={12} lg={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.recommendedProducts.showTitle"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.recommendedProducts.showTitle",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t(
                    "pages.dsokDetails.products.recommendedProducts.showTitle"
                  )}
                  checked={
                    values.settings?.smartkiosk?.productSettings
                      ?.recommendedProducts?.showTitle
                  }
                />
              </Div>
            </Grid>
            <Grid item xs={12} md={4} lg={6}>
              <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                <JumboTextField
                  select
                  required
                  InputLabelProps={{ required: false }}
                  name="settings.smartkiosk.productSettings.recommendedProducts.viewType"
                  label={t(
                    "pages.dsokDetails.products.recommendedProducts.viewType"
                  )}
                >
                  {RECOMMENDED_PRODUCTS_VIEW_TYPES.map((viewType, index) => {
                    return (
                      <MenuItem key={index} value={viewType.code}>
                        {viewType.name}
                      </MenuItem>
                    );
                  })}
                </JumboTextField>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                <JumboTextField
                  type="number"
                  InputLabelProps={{ required: false }}
                  required
                  fullWidth
                  name="settings.smartkiosk.productSettings.recommendedProducts.columns"
                  label={t(
                    "pages.dsokDetails.products.recommendedProducts.columns"
                  )}
                  InputProps={{
                    value:
                      values.settings?.smartkiosk?.productSettings
                        ?.recommendedProducts?.columns,
                    onChange: (e) =>
                      setFieldValue(
                        "settings.smartkiosk.productSettings.recommendedProducts.columns",
                        e.target.value
                      ),
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <Typography sx={{ width: "33%", flexShrink: 0 }}>
              {t("pages.dsokDetails.products.detail.title")}
            </Typography>
            <Grid item xs={12} md={12} lg={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.detail.showSku"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.detail.showSku",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t("pages.dsokDetails.products.detail.showSku")}
                  checked={
                    values.settings?.smartkiosk?.productSettings?.detail
                      ?.showSku
                  }
                />
              </Div>
            </Grid>
            <Grid item xs={12} md={4} lg={12}>
              <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                <FormControlLabel
                  control={
                    <Field
                      name="settings.smartkiosk.productSettings.detail.showBrand"
                      component={Switch}
                      onChange={(e) =>
                        setFieldValue(
                          "settings.smartkiosk.productSettings.detail.showBrand",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t("pages.dsokDetails.products.detail.showBrand")}
                  checked={
                    values.settings?.smartkiosk?.productSettings?.detail
                      ?.showBrand
                  }
                />
              </Div>
            </Grid>
          </Grid>
        </Grid>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.products.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};
export default SmartkioskProducts;
